import type { TFunction } from 'i18next';

import { DataTestIds } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSDialog } from '@repo/sds/SDSDialog';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSText } from '@repo/sds/SDSText';
import { SDSTextInput } from '@repo/sds/SDSTextInput';
import { SDSUserBadge } from '@repo/sds/SDSUserBadge';
import { GoogleIcon } from '@repo/ui/assets/icons/GoogleIcon';

import type { OauthProfile } from './mfa-settings-dialogs';
import { confirmActionState } from './mfa-settings-dialogs-utils';
import { MfaSettingsPage } from './mfa-settings-page';

const MFA = DataTestIds.COMMON.SECURITY.MFA;

export function MfaReauthDialog({
  actionError,
  busy,
  hasPassword,
  isMobile,
  oauthProfile,
  onCancel,
  onOauthContinue,
  onPasswordChange,
  onSubmitReauth,
  password,
  t,
}: {
  actionError?: string;
  busy: boolean;
  hasPassword: boolean;
  isMobile: boolean;
  oauthProfile: OauthProfile;
  onCancel: () => void;
  onOauthContinue: () => void;
  onPasswordChange: (value: string) => void;
  onSubmitReauth: () => void;
  password: string;
  t: TFunction<'common'>;
}) {
  const errorText = actionError ? (
    <SDSText color="alert" dataTestId={MFA.ERROR} text={actionError} type="body-small" />
  ) : null;

  const title = t('security.mfa.reauthTitle');
  const continueState = confirmActionState(busy, password.length === 0);
  const passwordFields = (
    <SDSFlexbox direction="column" gap="text-medium">
      <SDSTextInput
        dataTestId={MFA.REAUTH_PASSWORD}
        isFullWidth
        isRequired
        label={t('security.mfa.reauthPasswordLabel')}
        onChange={onPasswordChange}
        placeholder={t('security.mfa.reauthPasswordPlaceholder')}
        type="password"
        value={password}
      />
      {errorText}
    </SDSFlexbox>
  );
  const oauthFields = (
    <SDSFlexbox direction="column" gap="content-default">
      <SDSText text={t('security.mfa.reauthOauthLabel')} type="body-medium" />
      <SDSFlexbox className="MfaReauthOauth__account" justifyContent="space-between" alignItems="center">
        <SDSUserBadge avatarSrc={oauthProfile.avatarSrc} description={oauthProfile.email} name={oauthProfile.name} />
        <span className="MfaReauthOauth__google">
          <GoogleIcon />
        </span>
      </SDSFlexbox>
      <SDSButton
        dataTestId={MFA.OAUTH_CONTINUE}
        isFullWidth
        isSubmitting={busy}
        onClick={onOauthContinue}
        text={t('security.mfa.continueAs', { name: oauthProfile.continueAsName })}
      />
      <SDSButton
        dataTestId={MFA.OAUTH_OTHER_ACCOUNT}
        isFullWidth
        mode="ghost"
        onClick={onOauthContinue}
        text={t('security.mfa.useAnotherGoogle')}
      />
      {errorText}
    </SDSFlexbox>
  );
  const fields = hasPassword ? passwordFields : oauthFields;
  if (isMobile) {
    return (
      <MfaSettingsPage
        actions={
          hasPassword ? (
            <>
              <SDSButton dataTestId={MFA.CANCEL} mode="outline" onClick={onCancel} text={t('security.mfa.cancel')} />
              <SDSButton
                dataTestId={MFA.CONFIRM}
                isDisabled={continueState.disabled}
                isSubmitting={busy}
                onClick={onSubmitReauth}
                text={t('security.mfa.continue')}
              />
            </>
          ) : undefined
        }
        ariaLabel={title}
        backLabel={t('security.mfa.backToSecurity')}
        dataTestId={MFA.REAUTH_DIALOG}
        onBack={onCancel}
      >
        <SDSText text={title} type="h5" />
        {fields}
      </MfaSettingsPage>
    );
  }
  return (
    <SDSDialog
      dataTestId={MFA.REAUTH_DIALOG}
      isOpen
      onClose={onCancel}
      primaryAction={
        hasPassword
          ? {
              dataTestId: MFA.CONFIRM,
              ...continueState,
              label: t('security.mfa.continue'),
              onClick: onSubmitReauth,
            }
          : undefined
      }
      secondaryAction={
        hasPassword ? { dataTestId: MFA.CANCEL, label: t('security.mfa.cancel'), onClick: onCancel } : undefined
      }
      size="small"
      title={title}
    >
      {fields}
    </SDSDialog>
  );
}
